export const translations = {
  en: {
    nav: {
      home: "Home",
      about: "About Me",
      experience: "Experience",
      education: "Education",
      skills: "Skills",
      languages: "Languages",
      portfolio: "Portfolio",
      publications: "Publications",
      contact: "Contact",
    },
    home: {
      welcome: "Welcome",
      subtitle: "based in Belo Horizonte City, Minas Gerais State, Brazil.",
      cta: "Contact Me",
      typewriter: [
        "I'm Daniel B. Oliveira.",
        "I'm a Mechanical Engineer.",
        "I'm a Project Manager.",
        "I'm a Programmer.",
      ],
    },
    about: {
      title: "About Me",
      heading: "I'm Daniel B. Oliveira, a Mechanical Engineer.",
      intro1:
        "Mechanical Engineer with a master's degree in Structural Integrity and Dynamics and a postgraduate degree in Project Management, with experience since 2017 in utility systems, piping, industrial projects, and mining. I provide technical leadership and manage multidisciplinary teams, focusing on planning, scope, deadlines, schedules, the quality of technical documents, coordination with clients and suppliers, 3D model analysis, technical assessments, and support for field execution. I have experience working on projects for major mining companies, complemented by expertise in FEM, DEM, and coupled DEM-FEM simulations, engineering software development, piping flexibility analysis, and industrial hydraulic systems.",
      intro2:
        "",
      name: "Name:",
      email: "Email:",
      age: "Age:",
      from: "From:",
      download: "Download CV",
      experience: "Years of Experience",
      projects: "Executed Projects",
    },
    education: {
      title: "Education",
      items: [
        {
          yearRange: "2016 - 2019",
          title: "Master’s degree",
          place: "Pontifical Catholic University of Minas Gerais",
          desc: "I researched the coupling between the finite element method and the discrete element method using open-source software. I used code_aster software as a finite element structural solver and LIGGGHTS-PUBLIC software as a discrete element solver. My work consisted of performing coupled simulations between the two solvers to analyze the material flow and structural load in car dumpers.",
        },
        {
          yearRange: "2022 - 2023",
          title: "Postgraduate",
          place: "Pontifical Catholic University of Minas Gerais",
          desc: "The course has the objective to training specialists in Project Management following the PMI (Project Management Institute) standard disseminated by the PMBOK, capable of articulating strategic and practical thinking about project management, and highlighting the multidisciplinary character by involving themes and subjects from different areas (human, exact and biological).",
        },
        {
          yearRange: "2010 - 2016",
          title: "Undergraduate",
          place: "Newton Paiva University Center",
          desc: "Mechanical Engineering aims to prepare students so that they can work in the structure of processes in companies — from the design of new machines, equipment and parts for the industry to the inspection of the production line, monitoring of maintenance and carrying out tests with the machinery.",
        },
      ],
    },
    experience: {
      title: "Experience",
      items: [
        {
          yearRange: "06/2026 - Present",
          title: "Utilities Systems Coordinator",
          company: {
            name: "Ausenco",
            url: "https://ausenco.com/",
          },
          place: "Belo Horizonte, Minas Gerais, Brazil",
          desc: `Key responsibilities:

• Managing the execution team's time and scope.
• Planning personnel allocation.
• Reviewing and adjusting schedules to ensure alignment with project milestones.
• Preparing execution progress reports.
• Providing technical and managerial coordination with clients, suppliers, and internal teams.
• Reviewing and commenting on 3D models of utility systems and equipment.
• Engaging with suppliers to select and specify equipment.
• Ensuring the quality of engineering deliverables.
• Preparing and managing technical proposals and contract amendments.
• Preparing and reviewing engineering documentation.
• Conducting site visits and field inspections.
• Developing technical assessments and recommendations for utility equipment.
• Reviewing and validating documentation submitted by suppliers.`,
        },
        {
          yearRange: "04/2024 - 06/2026",
          title: "Utilities Systems Lead",
          company: {
            name: "Brass Engineering",
            url: "https://brassengineering.com/",
          },
          place: "Belo Horizonte, Minas Gerais, Brazil",
          desc: `Key responsibilities:

• Managing the execution team's time and scope.
• Planning personnel allocation.
• Reviewing and adjusting schedules to ensure alignment with project milestones.
• Preparing execution progress reports.
• Providing technical and managerial coordination with clients, suppliers, and internal teams.
• Reviewing and commenting on 3D models of utility systems and equipment.
• Engaging with suppliers to select and specify equipment.
• Ensuring the quality of engineering deliverables.
• Preparing and managing technical proposals and contract amendments.
• Preparing and reviewing engineering documentation.
• Conducting site visits and field inspections.
• Developing technical assessments and recommendations for utility equipment.
• Reviewing and validating documentation submitted by suppliers.

Projects:

• Tailings Disposal Structure 9 – EDR9 (Basic Engineering), Araxá, Minas Gerais, Brazil, 2021, basic engineering, greenfield, CBMM.
• EDR9 Administrative Areas, Araxá, Minas Gerais, Brazil, 2021, conceptual engineering, greenfield, CBMM.
• Tailings Filtration Plant – Dry Tail (IOB-GEP-18-BE-0022), Conceição do Mato Dentro, Minas Gerais, Brazil, 2021, detailed engineering, greenfield, Anglo American.
• Underflow and Junior Slurry Pipeline Pump Upgrade, Conceição do Mato Dentro, Minas Gerais, Brazil, 2024, detailed engineering, greenfield, Anglo American.
• EDR9 Complementary Units, Araxá, Minas Gerais, Brazil, 2022, detailed engineering, greenfield, CBMM.
• EDR9 ETEL III, Araxá, Minas Gerais, Brazil, 2024, conceptual engineering, greenfield, CBMM.
• C2 Modifications and Filtration Phase 2 (CR-G-OEN-AD-343), Mariana, Minas Gerais, Brazil, 2024, detailed engineering, greenfield, Samarco.
• C1 Revamp and Filtration Phase 3, Mariana, Minas Gerais, Brazil, 2025, basic engineering, greenfield, Samarco.
• Tailings Filtration Phase III (C2 and C3), Mariana, Minas Gerais, Brazil, 2025, basic engineering, greenfield, Samarco.
• Administrative and Operational Buildings for the PDER C Project, Mariana, Minas Gerais, Brazil, 2025, basic engineering, greenfield, Samarco.
• In-Pit Storage – Temporary Pit, Conceição do Mato Dentro, Minas Gerais, Brazil, 2024, conceptual engineering, greenfield, Anglo American.
• Conceptual Slurry Blending Project, Conceição do Mato Dentro, Minas Gerais, Brazil, 2024, conceptual engineering, greenfield, Anglo American.
• In-Pit Storage – Temporary Pit (PFSA), Conceição do Mato Dentro, Minas Gerais, Brazil, 2025, basic engineering, greenfield, Anglo American.
• Concentration Plant Modifications to Reduce Iron in Tailings at Viga Mine, Congonhas, Minas Gerais, Brazil, 2025, basic engineering, brownfield, Vale.
• Ponta da Madeira Maritime Terminal Fire Protection System Compliance with PNR 15 and 17, São Luís, Maranhão, Brazil, 2024, basic engineering, brownfield, Vale.
• Ultrafine Tailings Pumping to Area 8, Ouro Preto, Minas Gerais, Brazil, 2024, basic engineering, greenfield, Vale.
• New Route for the Viga Tailings Pipeline, Congonhas, Minas Gerais, Brazil, 2025, basic engineering, brownfield, Vale.
• Study and Validation of the VGR 1 Ø14" Slurry Pumping System to the 1800PI Tailings Center, Nova Lima, Minas Gerais, Brazil, 2025, detailed engineering, brownfield, Vale.
• VGR 1 0.15 mm Circuit Optimization, Nova Lima, Minas Gerais, Brazil, 2025, conceptual engineering, brownfield, Vale.
• Pumping TS Increase to SL/SO (REJ0128), Oriximiná, Pará, Brazil, 2025, basic engineering, brownfield, MRN.
• Slurry Tailings Program – Tailings Pipeline, Conceição do Mato Dentro, Minas Gerais, Brazil, 2023, conceptual engineering, greenfield, Anglo American.
• Electrical Room HVAC System Verification, Araxá, Minas Gerais, Brazil, 2026, basic engineering, brownfield, CBMM.
• C3 Tailings Transportation System to Filtration Study, Mariana, Minas Gerais, Brazil, 2024, detailed engineering, greenfield, Samarco.
• PDE System 1 – Pit 345 (CR-G-OEN-AD-315), Mariana, Minas Gerais, Brazil, 2025, detailed engineering, greenfield, Samarco.
• SDR Alegria Sul 2, Mariana, Minas Gerais, Brazil, 2025, detailed engineering, greenfield, Samarco.
• Filtration 3 – Filtrate Pumping, Mariana, Minas Gerais, Brazil, 2025, detailed engineering, greenfield, Samarco.
• C1 Revamp, Mariana, Minas Gerais, Brazil, 2025, detailed engineering, greenfield, Samarco.
• Tailings Disposal Structure 9 – EDR9 (Detailed Engineering), Araxá, Minas Gerais, Brazil, 2021, detailed engineering, greenfield, CBMM.
• Replacement of Pneumatic Knife Gate Valves with Hydraulic Valves, Conceição do Mato Dentro, Minas Gerais, Brazil, 2026, detailed engineering, brownfield, Anglo American.
• Filtration DF Modifications, Conceição do Mato Dentro, Minas Gerais, Brazil, 2025, detailed engineering, greenfield, Anglo American.
• Engineering for Slurry Blending, Conceição do Mato Dentro, Minas Gerais, Brazil, 2025, basic engineering, greenfield, Anglo American.
• Pumping – Interlock Matrix, Conceição do Mato Dentro, Minas Gerais, Brazil, 2025, detailed engineering, greenfield, Anglo American.
• Filtration – Interlock Matrix, Conceição do Mato Dentro, Minas Gerais, Brazil, 2025, detailed engineering, greenfield, Anglo American.
• Relocation of Tank 51-TQ-07 and Ubu Pneumatic Conveying Project, Mariana, Minas Gerais, Brazil, 2025, basic engineering, greenfield, Samarco.
• B&A Waste Office Expansion, Barcarena, Pará, Brazil, 2025, basic engineering, greenfield, Hydro.
• Filtration Phase III (filtration building expansion, vacuum disc filter installation, and filter cake conveying systems), Mariana, Minas Gerais, Brazil, 2025, detailed engineering, greenfield, Samarco.
• Peer Review and Technical Support for Oman Sealing, Oman, 2026, detailed engineering, brownfield, Vale.
• Optical-Grade Capacity Upgrade from 920 TPA to 3,000 TPA, Araxá, Minas Gerais, Brazil, 2025, basic engineering, greenfield, CBMM.`,
        },
        {
          yearRange: "04/2023 - 03/2024",
          title: "Utilities Systems and Piping Lead",
          company: {
            name: "TRENG Project Consulting and Technology",
            url: "https://trengy.com.br/",
          },
          place: "Belo Horizonte, Minas Gerais, Brazil",
          desc: `Lead for the Utilities Systems and Piping disciplines on mining projects.

• Leading the utilities systems and piping disciplines on multidisciplinary mining projects.
• Reviewing execution schedules to establish realistic deadlines and allocate personnel efficiently.
• Developing detailed work plans with clear, structured, and achievable scopes.
• Providing technical support to engineering teams to improve development quality and overall efficiency.
• Participating actively in technical and management alignment meetings with clients and internal stakeholders.
• Supporting cost-estimating and budgeting teams by identifying technical requirements for proposals.
• Reviewing and assuring the quality of engineering documents and their compliance with project standards and deliverables.
• Coordinating candidate interviews for team expansion and qualification validation.
• Managing outsourced teams involved in engineering documentation.
• Continuously assessing team productivity through work-hour monitoring, performance indicators, and survey-based accountability metrics.

Projects:

• Pebble Recrushing, Godofredo Viana, Maranhão, Brazil, 2023, detailed engineering, brownfield, Aurizona.
• Water Pumping from Santo Antônio Dam to the Plant 1 Sampler, Paracatu, Minas Gerais, Brazil, 2023, basic engineering, greenfield, Kinross.
• Pumping from Tank C3 to C1, Paracatu, Minas Gerais, Brazil, 2023, conceptual/basic/detailed engineering, greenfield, Kinross.
• Pumping from Tank C3 to the Plant, Paracatu, Minas Gerais, Brazil, 2023, conceptual/basic/detailed engineering, greenfield, Kinross.
• Córrego Rico Pumping, Paracatu, Minas Gerais, Brazil, 2023, conceptual engineering, greenfield, Kinross.
• New Tailings Filtration Plant, Riacho dos Machados, Minas Gerais, Brazil, 2023, basic engineering, greenfield, Mineração Riacho dos Machados.
• Automatic Sprinkler Fire Protection System (SDACI) TR-1085KS-05, Canaã dos Carajás, Pará, Brazil, 2023, detailed engineering, brownfield, Vale.
• Service Water System – S11D Project, Canaã dos Carajás, Pará, Brazil, 2023, detailed engineering, brownfield, Vale.
• Fire Protection System Upgrade – Sossego Mine, Canaã dos Carajás, Pará, Brazil, 2023, detailed engineering, greenfield, Vale.
• HVAC Upgrade for the Vargem Grande Hydraulic Rooms, Nova Lima, Minas Gerais, Brazil, 2023, basic engineering, greenfield, Vale.
• Fire Protection System – Nova Era, Nova Era, Minas Gerais, Brazil, 2023, basic engineering, greenfield, Vale.
• Fire Protection System – Explosives Storage – Sossego, Canaã dos Carajás, Pará, Brazil, 2023, basic engineering, greenfield, Vale.
• New Vargem Grande Repulping Sump, Nova Lima, Minas Gerais, Brazil, 2023, basic engineering, greenfield, Vale.
• Fire Protection System – Porto Velho, Nova Era, Minas Gerais, Brazil, 2023, detailed engineering, greenfield, Vale.
• Compressor Room Upgrade – Grinding and Additives Area – Ponta da Madeira, São Luís, Pará, Brazil, 2023, basic engineering, greenfield, Vale.
• Fire Protection System – Distribution Center in Barão de Cocais, Barão de Cocais, Minas Gerais, Brazil, 2023, basic engineering, greenfield, Vale.
• Welding Yard Renovation – Governador Valadares, Governador Valadares, Minas Gerais, Brazil, 2023, detailed engineering, greenfield, Vale.
• Timbopeba Loadout Silo Sprinkling Plant, Ouro Preto, Minas Gerais, Brazil, 2023, detailed engineering, greenfield, Vale.
• DEM Study of the S11D Link Conveyor, Canaã dos Carajás, Minas Gerais, Brazil, 2023, basic engineering, greenfield, Vale.
• Brucutu Mine AVCB Compliance, São Gonçalo do Rio Abaixo, Minas Gerais, Brazil, 2023, detailed engineering, greenfield, Vale.`,
        },
        {
          yearRange: "08/2021 - 04/2023",
          title: "Utilities Systems Engineer",
          company: {
            name: "Brass Engineering",
            url: "https://brassengineering.com/",
          },
          place: "Belo Horizonte, Minas Gerais, Brazil",
          desc: `Developed projects for the Utilities Systems discipline, including engineering documents such as calculation reports, P&IDs, technical specifications for equipment and services, and definitions of project standards and criteria, while continuously monitoring project planning. Conducted internal and external technical alignment meetings and presented projects to clients and peers.

Projects:

• Tailings Dam No. 8, Araxá, Minas Gerais, Brazil, 2023, basic engineering, greenfield, CBMM.
• Tailings Dam No. 9, Araxá, Minas Gerais, Brazil, 2023, basic engineering, greenfield, CBMM.
• Homogenization Yard Expansion, Araxá, Minas Gerais, Brazil, 2023, detailed engineering, greenfield, CBMM.
• Relocation of Support Structures at Periquito Mine, Itabira, Minas Gerais, Brazil, 2023, conceptual engineering, greenfield, Vale.
• Timbopeba Total Tailings Filtration, Ouro Preto, Minas Gerais, Brazil, 2023, conceptual engineering, greenfield, Vale.
• Fire Protection System for the Ore Moisture Reduction System, São Luís, Maranhão, Brazil, 2023, basic engineering, greenfield, Vale.
• Production Capacity Increase, Oriximiná, Pará, Brazil, 2023, conceptual engineering, greenfield, MRN.
• Tailings Disposal and Industrial Drainage, Conceição do Mato Dentro, Minas Gerais, Brazil, 2023, basic engineering, greenfield, Anglo American.
• EB2 Reagent Dosing, Conceição do Mato Dentro, Minas Gerais, Brazil, 2023, basic engineering, greenfield, Anglo American.
• Raw Materials Storage System, Conceição do Mato Dentro, Minas Gerais, Brazil, 2023, basic engineering, greenfield, CBMM.`,
        },
        {
          yearRange: "02/2021 - 08/2021 (7 months)",
          title: "Utilities Systems Engineer",
          company: {
            name: "TRENG Project Consulting and Technology",
            url: "https://trengy.com.br/",
          },
          place: "Belo Horizonte, Minas Gerais, Brazil",
          desc: `Developed technical documents for HVAC projects, performed structural calculations using the finite element method (FEM), and analyzed flow structures using the discrete element method (DEM). Produced project documents including calculation reports and technical specifications for equipment and services, and defined project standards and criteria. Also conducted internal and external technical alignment meetings and delivered project presentations to clients and peers.

Projects:

• Rolling Mill II HVAC, Marabá, Pará, Brazil, 2021, detailed engineering, greenfield, Sinobras.
• Salobo Fuel Station Renovation, Marabá, Pará, Brazil, 2021, detailed engineering, greenfield, Vale.
• Flow Simulation and Discharge Chute Redesign – TR-1080KS-37, Brazil, 2021, basic engineering, greenfield, Vale.
• Flow Simulation and Discharge Chute Redesign – TR-1081KS-11@14, Brazil, 2021, basic engineering, greenfield, Vale.
• Mutuca Mine Central Office HVAC, Nova Lima, Minas Gerais, Brazil, 2021, basic engineering, greenfield, Vale.
• Brucutu Mine Cafeteria HVAC, São Gonçalo do Rio Abaixo, Minas Gerais, Brazil, 2021, basic engineering, greenfield, Vale.
• Cauê Mine Temporary Training Center HVAC, Itabira, Minas Gerais, Brazil, 2021, detailed engineering, greenfield, Vale.
• Conceição Mine Cafeteria HVAC, Itabira, Minas Gerais, Brazil, 2021, basic engineering, greenfield, Vale.
• Conceição Mine Locker Room HVAC, Itabira, Minas Gerais, Brazil, 2021, basic engineering, greenfield, Vale.
• Brucutu Mine Cafeteria HVAC, São Gonçalo do Rio Abaixo, Minas Gerais, Brazil, 2021, basic engineering, greenfield, Vale.
• Flow Simulation and Discharge Chute Redesign – TR-1081KS-11@14, Brazil, 2021, basic engineering, greenfield, Vale.`,
        },
        {
          yearRange: "02/2020 - 02/2021 (1 year, 1 month)",
          title: "Mechanical Engineer",
          company: {
            name: "OpenCAE Engineering",
            url: null,
          },
          place: "Belo Horizonte, Minas Gerais, Brazil",
          desc: "Developed conceptual DEM engineering software and simulated granular-flow systems for mining companies, in addition to conducting FEM structural analyses.",
        },
        {
          yearRange: "02/2017 - 02/2020 (3 years, 1 month)",
          title: "Mechanical Engineer",
          company: {
            name: "Conep Project Consulting",
            url: "https://conep.com.br/",
          },
          place: "Belo Horizonte, Minas Gerais, Brazil",
          desc: `Worked on structural and mechanical systems projects using the finite element method (FEM).
Designed and assessed granular-material conveying systems using the discrete element method (DEM).
Performed design and flexibility calculations for piping and pumping systems.
Completed numerous hydraulic projects for mining and steel companies.`,
        },
        {
          yearRange: "01/2016 - 01/2017 (1 year, 1 month)",
          title: "Technical Sales Representative",
          company: {
            name: "Oxiban Industrial Gases",
            url: null,
          },
          place: "Belo Horizonte, Minas Gerais, Brazil",
          desc: "Assembled and prepared oxy-acetylene welding equipment. Prospected for new customers, closed deals, and provided high-quality customer service to build loyalty. Also managed after-sales support and stock replenishment to maintain adequate inventory levels.",
        },
      ],
    },
    skills: {
      title: "Skills",
      items: [
        { name: "Agile & Waterfall Methodologies", percent: 100 },
        { name: "Leadership", percent: 100 },
        { name: "Open-Source Software", percent: 100 },
        { name: "Research Skills", percent: 100 },
        { name: "Conflict Resolution", percent: 100 },
        { name: "Finite Element Method", percent: 100 },
        { name: "Microsoft Excel", percent: 100 },
        { name: "Microsoft Word", percent: 100 },
        { name: "Programming", percent: 100 },
        { name: "Linux", percent: 100 },
        { name: "Python", percent: 100 },
        { name: "C++", percent: 100 },
        { name: "Matlab", percent: 100 },
        { name: "Discrete Element Method", percent: 100 },
        { name: "Altair HyperWorks", percent: 100 },
        { name: "Paraview", percent: 100 },
        { name: "LIGGGHTS-PUBLIC", percent: 100 },
        { name: "Libmesh", percent: 100 },
        { name: "Code_aster", percent: 100 },
        { name: "Salome-Meca", percent: 100 },
        { name: "Bentley AutoPipe", percent: 100 },
        { name: "AFT Fathom", percent: 100 },
        { name: "HVAC", percent: 100 },
        { name: "Solidworks", percent: 100 },
        { name: "Autocad", percent: 100 },
        { name: "Latex", percent: 100 },
        { name: "Maple", percent: 100 },
        { name: "Proteus Isis", percent: 100 },
        { name: "Arduino", percent: 100 },
        { name: "Raspberry Pi", percent: 100 },
      ],
    },
    languages: {
      title: "Languages",
      items: [
        { name: "Portuguese", level: "Native", percent: 100 },
        { name: "English", level: "Professional Proficient", percent: 80 },
        { name: "French", level: "Basic", percent: 20 },
      ],
    },
    portfolio: {
      title: "My Work",
      all: "All",
      category: "Category",
    },
    publications: {
      title: "Publications",
    },
    contact: {
      title: "Contact",
      addressTitle: "Address",
      followTitle: "Follow Me",
      sendTitle: "Send me a note",
      form: {
        name: "Name",
        email: "Email",
        message: "Tell me more about your needs........",
        submit: "Send Message",
        sending: "Sending.....",
      },
      toastSuccess: "Message sent successfully!",
      toastError: "Something went wrong!",
    },
    footer: {
      rights: "All Rights Reserved.",
    },
    general: {
      changeLanguage: "Change language",
    },
  },
  pt: {
    nav: {
      home: "Início",
      about: "Sobre Mim",
      experience: "Experiência",
      education: "Formação",
      skills: "Competências",
      languages: "Idiomas",
      portfolio: "Portfólio",
      publications: "Publicações",
      contact: "Contato",
    },
    home: {
      welcome: "Bem-vindo",
      subtitle: "baseado em Belo Horizonte, Minas Gerais, Brasil.",
      cta: "Entre em contato",
      typewriter: [
        "Eu sou Daniel B. Oliveira.",
        "Eu sou Engenheiro Mecânico.",
        "Eu sou Gerente de Projetos.",
        "Eu sou Programador.",
      ],
    },
    about: {
      title: "Sobre Mim",
      heading: "Sou Daniel B. Oliveira, engenheiro mecânico.",
      intro1:
        "Engenheiro Mecânico, mestre em Integridade Estrutural e Dinâmica e pós-graduado em Gerenciamento de Projetos, com experiência desde 2017 em Sistemas de Utilidades, Tubulação, projetos industriais e mineração. Atuo na liderança técnica e gestão de equipes multidisciplinares, com foco em planejamento, escopo, prazos, cronogramas, qualidade de documentos técnicos, interface com clientes e fornecedores, análise de modelos 3D, pareceres técnicos e suporte à execução em campo. Possuo vivência em projetos para grandes mineradoras e experiência complementar em simulações FEM, DEM, DEM-FEM, desenvolvimento de softwares para engenharia, flexibilidade de tubulações e sistemas hidráulicos industriais.",
      intro2:
        "",
      name: "Nome:",
      email: "E-mail:",
      age: "Idade:",
      from: "Origem:",
      download: "Baixar CV",
      experience: "Anos de experiência",
      projects: "Projetos executados",
    },
    education: {
      title: "Formação",
      items: [
        {
          yearRange: "2016 - 2019",
          title: "Mestrado",
          place: "Pontifícia Universidade Católica de Minas Gerais",
          desc: "Pesquisei o acoplamento entre o método dos elementos finitos e o método dos elementos discretos usando software de código aberto. Usei o software code_aster como solucionador estrutural por elementos finitos e o software LIGGGHTS-PUBLIC como solucionador por elementos discretos. Meu trabalho consistiu em realizar simulações acopladas entre os dois solvers para analisar o fluxo de material e a carga estrutural em descarregadores de vagões.",
        },
        {
          yearRange: "2022 - 2023",
          title: "Pós-graduação",
          place: "Pontifícia Universidade Católica de Minas Gerais",
          desc: "O curso tem como objetivo formar especialistas em Gestão de Projetos seguindo o padrão do PMI (Project Management Institute) difundido pelo PMBOK, capazes de articular pensamento estratégico e prático sobre gestão de projetos, destacando o caráter multidisciplinar por envolver temas e disciplinas de diferentes áreas (humanas, exatas e biológicas).",
        },
        {
          yearRange: "2010 - 2016",
          title: "Graduação",
          place: "Centro Universitário Newton Paiva",
          desc: "A Engenharia Mecânica busca preparar os estudantes para atuarem na estrutura dos processos nas empresas — desde o projeto de novas máquinas, equipamentos e peças para a indústria até a inspeção da linha de produção, acompanhamento da manutenção e realização de testes com as máquinas.",
        },
      ],
    },
    experience: {
      title: "Experiência",
      items: [
        {
          yearRange: "06/2026 - Atual",
          title: "Coorndenador de Sistemas de Utilidades",
          company: {
            name: "Ausenco",
            url: "https://ausenco.com/",
          },
          place: "Belo Horizonte, Minas Gerais, Brasil",
          desc: `Principais responsabilidades:

• Gestão de tempo e escopo da equipe de execução.
• Planejamento da alocação de pessoas.
• Verificação e ajustes de cronograma para garantir alinhamento aos marcos do projeto.
• Preparação de relatórios de progresso da execução.
• Coordenação técnica e gerencial com clientes, fornecedores e equipes internas.
• Revisão e comentários em modelos 3D de sistemas de utilidades e equipamentos.
• Interação com fornecedores para seleção e especificação de equipamentos.
• Garantia da qualidade dos entregáveis de engenharia.
• Elaboração e gestão de propostas técnicas e aditivos contratuais.
• Preparação e revisão de documentação de engenharia.
• Realização de visitas e inspeções em campo.
• Desenvolvimento de avaliações técnicas e recomendações para equipamentos de utilidades.
• Análise e validação de documentação enviada por fornecedores.`,
        },
        {
          yearRange: "04/2024 - 06/2026",
          title: "Líder de Sistemas de Utilidades",
          company: {
            name: "Brass Engineering",
            url: "https://brassengineering.com/",
          },
          place: "Belo Horizonte, Minas Gerais, Brasil",
          desc: `Principais responsabilidades:

• Gestão de tempo e escopo da equipe de execução.
• Planejamento da alocação de pessoas.
• Verificação e ajustes de cronograma para garantir alinhamento aos marcos do projeto.
• Preparação de relatórios de progresso da execução.
• Coordenação técnica e gerencial com clientes, fornecedores e equipes internas.
• Revisão e comentários em modelos 3D de sistemas de utilidades e equipamentos.
• Interação com fornecedores para seleção e especificação de equipamentos.
• Garantia da qualidade dos entregáveis de engenharia.
• Elaboração e gestão de propostas técnicas e aditivos contratuais.
• Preparação e revisão de documentação de engenharia.
• Realização de visitas e inspeções em campo.
• Desenvolvimento de avaliações técnicas e recomendações para equipamentos de utilidades.
• Análise e validação de documentação enviada por fornecedores.

Projetos em que participei:

• Projeto Estrutura de Disposição de Rejeitos - EDR9 (Básico), Araxá, Minas Gerais, Brasil, 2021, Básico, Green Field,  CBMM.
• Projeto Áreas Administrativas da EDR9, Araxá, Minas Gerais, Brasil, 2021,  Conceitual, Green Field,  CBMM.
• Projeto Planta de Filtragem do Rejeito - Dry Tail (IOB-GEP-18-BE-0022), Conceição do Mato Dentro, Minas Gerais, Brasil, 2021,  Detalhado, Green Field,  Anglo American.
• Projeto Repotenciamento das Bombas do Underflow e Mineroduto Junior, Conceição do Mato Dentro, Minas Gerais, Brasil, 2024,  Detalhado, Green Field,  Anglo American.
• Projeto Unidades Complementares da EDR 9, Araxá, Minas Gerais, Brasil, 2022,  Detalhado, Green Field,  CBMM.
• Projeto ETEL III da EDR 9, Araxá, Minas Gerais, Brasil, 2024,  Conceitual, Green Field,  CBMM.
• Projeto Adequações C2 e Filtragem Fase 2 (CR-G-OEN-AD-343), Mariana, Minas Gerais, Brasil, 2024,  Detalhado, Green Field,  Samarco.
• Projeto Revamp C1 e Filtragem Fase 3, Mariana, Minas Gerais, Brasil, 2025,  Básico, Green Field,  Samarco.
• Projeto Filtragem de Rejeito Fase III (C2 e C3), Mariana, Minas Gerais, Brasil, 2025,  Básico, Green Field,  Samarco.
• Projeto Edificações Administrativas e Operacionais do Projeto da PDER C, Mariana, Minas Gerais, Brasil, 2025,  Básico, Green Field,  Samarco.
• Projeto Armazenamento em Cavas - Cava Temporária, Conceição do Mato Dentro, Minas Gerais, Brasil, 2024,  Conceitual, Green Field,  Anglo American.
• Projeto Conceitual para Mistura de Lamas, Conceição do Mato Dentro, Minas Gerais, Brasil, 2024,  Conceitual, Green Field,  Anglo American.
• Projeto Armazenamento em Cavas - Cava Temporária (PFSA), Conceição do Mato Dentro, Minas Gerais, Brasil, 2025,  Básico, Green Field,  Anglo American.
• Projeto Adequações na Usina de Concentração para a Redução de Ferro no Rejeito na Mina de Viga, Congonhas, Minas Gerais, Brasil, 2025,  Básico, Brown Field,  Vale.
• Projeto Adequação às PNRs 15 e 17 do Sistema de Proteção e Combate a Incêndio – SPCI do Terminal Marítimo de Ponta da Madeira, São Luís, Maranhão, Brasil, 2024,  Básico, Brown Field,  Vale.
• Projeto Bombeamento de Rejeito Ultrafino para a Área 8, Ouro Preto, Minas Gerais, Brasil, 2024,  Básico, Green Field,  Vale.
• Projeto Nova Rota do Rejeitoduto de Viga, Congonhas, Minas Gerais, Brasil, 2025,  Básico, Brown Field,  Vale.
• Projeto Estudo e Validação do Sistema de Bombeamento de Lamas VGR 1 Ø14" até a Central de Rejeitos 1800PI, Nova Lima, Minas Gerais, Brasil, 2025,  Detalhado, Brown Field,  Vale.
• Projeto Otimização do Circuito 0,15 mm de VGR 1, Nova Lima, Minas Gerais, Brasil, 2025,  Conceitual, Brown Field,  Vale.
• Projeto Aumento do TS de Bombeamento para SL/SO (REJ0128), Oriximiná, Pará, Brasil, 2025,  Básico, Brown Field,  MRN.
• Projeto Programa Rejeitos de Lamas - Rejeitoduto, Conceição do Mato Dentro, Minas Gerais, Brasil, 2023,  Conceitual, Green Field,  Anglo American.
• Projeto Verificação de Sistema HVAC de Sala Elétrica, Araxá, Minas Gerais, Brasil, 2026,  Básico, Brown Field,  CBMM.
• Projeto Estudo Sistema de Transporte de Rejeito C3 para Filtragem, Mariana, Minas Gerais, Brasil, 2024,  Detalhado, Green Field,  Samarco.
• Projeto Sistema 1 PDE - Cava 345(CR-G-OEN-AD-315), Mariana, Minas Gerais, Brasil, 2025,  Detalhado, Green Field, Samarco.
• Projeto SDR Alegria Sul 2, Mariana, Minas Gerais, Brasil, 2025,  Detalhado, Green Field, Samarco.
• Projeto Filtragem 3 - Bombeamento Filtrado, Mariana, Minas Gerais, Brasil, 2025,  Detalhado, Green Field, Samarco.
• Projeto Revamp C1, Mariana, Minas Gerais, Brasil, 2025,  Detalhado, Green Field, Samarco.
• Projeto Estrutura de Disposição de Rejeitos - EDR9 (Detalhado), Araxá, Minas Gerais, Brasil, 2021,  Detalhado, Green Field,  CBMM.
• Projeto Substituição de Válvulas Guilhotinas Pneumáticas por Hidráulicas, Conceição do Mato Dentro, Minas Gerais, Brasil, 2026,  Detalhado, Brown Field,  Anglo American.
• Projeto Adequação de DFs Filtragem, Conceição do Mato Dentro, Minas Gerais, Brasil, 2025,  Detalhado, Green Field,  Anglo American.
• Projeto Engenharia para Mistura de Lamas, Conceição do Mato Dentro, Minas Gerais, Brasil, 2025,  Básico, Green Field,  Anglo American.
• Projeto Bombeamento - Matriz de Bloqueio, Conceição do Mato Dentro, Minas Gerais, Brasil, 2025,  Detalhado, Green Field,  Anglo American.
• Projeto Filtragem - Matriz de Bloqueio, Conceição do Mato Dentro, Minas Gerais, Brasil, 2025,  Detalhado, Green Field,  Anglo American.
• Projeto Realocação Tanque 51-TQ-07 e Projeto Transporte Pneumático Ubu, Mariana, Minas Gerais, Brasil, 2025,  Básico, Green Field,  Samarco.
• Projeto Ampliação do Escritório de Resíduos B&A, Barcarena, Pará, Brasil, 2025,  Básico, Green Field,  Hydro.
• Projeto Filtragem Fase III (Ampliação Prédio Filtragem, Instalação Filtros de Disco à  Vácuo,  Sistemas de Transporte da Torta Filtrada), Mariana, Minas Gerais, Brasil, 2025,  Detalhado, Green Field,  Samarco.
• Projeto Peer Review e Suporte Técnico Selagem de Omã, -, -, Omã, 2026,  Detalhado, Brown Field,  Vale.
• Projeto Revamp Grau Ótico de 920 TPA para 3000 TPA, Araxá, Minas Gerais, Brasil, 2025,  Básico, Green Field,  CBMM.`,
        },
        {
          yearRange: "04/2023 - 03/2024",
          title: "Líder de Sistemas de Utilidades e Tubulações",
          company: {
            name: "TRENG Consultoria e Tecnologia em Projetos",
            url: "https://trengy.com.br/",
          },
          place: "Belo Horizonte, Minas Gerais, Brasil",
          desc: `Líder das disciplinas de Sistemas de Utilidades e Tubulações em projetos de mineração

• Liderança das disciplinas de sistemas de utilidades e tubulações em projetos multidisciplinares para a indústria de mineração.
• Análise de cronogramas de execução para definir prazos realistas e garantir alocação eficiente de pessoal.
• Desenvolvimento de planos de trabalho detalhados para estabelecer escopos claros, estruturados e viáveis.
• Suporte técnico às equipes de engenharia, com foco no aumento da qualidade e eficiência do desenvolvimento.
• Participação ativa em reuniões de alinhamento técnico e gerencial com clientes e partes interessadas internas.
• Suporte às equipes de estimativa de custos e orçamento por meio da identificação de requisitos técnicos para elaboração de propostas.
• Revisão e garantia da qualidade de documentos de engenharia para assegurar conformidade com padrões e entregáveis do projeto.
• Coordenação de entrevistas de candidatos para expansão da equipe e validação de qualificações.
• Gestão de equipes terceirizadas envolvidas na preparação de documentação de engenharia.
• Avaliação contínua da produtividade da equipe, incluindo acompanhamento de horas trabalhadas, indicadores de desempenho e prestação de contas por métricas baseadas em pesquisas.

Projetos em que participei:

• Projeto Rebritagem de Pebbles, Godofredo Viana, Maranhão, Brasil, 2023,  Detalhado, Brown Field,  Aurizona.
• Projeto Bombeamento de Água da Barragem Santo Antônio para o Amostrador da Planta 1, Paracatu, Minas Gerais, Brasil, 2023,  Básico, Green Field,  Kinross.
• Projeto Bombeamento do Tanque C3 para o C1, Paracatu, Minas Gerais, Brasil, 2023,  Conceitual/Básico/Detalhado, Green Field,  Kinross.
• Projeto Bombeamento do Tanque C3 para a Planta, Paracatu, Minas Gerais, Brasil, 2023,  Conceitual/Básico/Detalhado, Green Field,  Kinross.
• Projeto Bombeamento do Córrego Rico, Paracatu, Minas Gerais, Brasil, 2023,  Conceitual, Green Field,  Kinross.
• Projeto Nova Planta de Filtragem de Rejeito, Riacho dos Machados, Minas Gerais, Brasil, 2023,  Básico, Green Field,  Mineração Riacho dos Machados.
• Projeto Sistema de Proteção Contra Incêndio com chuveiros automáticos (Vale) (SDACI) TR-1085KS-05, Canaã dos Carajás, Pará, Brasil, 2023,  Detalhado, Brown Field,  Vale.
• Projeto Sistema de Água de Serviço – Projeto S11D, Canaã dos Carajás, Pará, Brasil, 2023,  Detalhado, Brown Field,  Vale.
• Projeto Atualização do Sistema de Proteção Contra Incêndio – Mina do Sossego, Canaã dos Carajás, Pará, Brasil, 2023,  Detalhado, Green Field,  Vale.
• Projeto Adequação do Ar-Condicionado das Salas Hidráulicas de Vargem Grande, Nova Lima, Minas Gerais, Brasil, 2023,  Básico, Green Field,  Vale.
• Projeto Sistema de Proteção Contra Incêndio – Nova Era, Nova Era, Minas Gerais, Brasil, 2023,  Básico, Green Field,  Vale.
• Projeto Sistema de Proteção Contra Incêndio – Armazenamento de Explosivos – Sossego, Canaã dos Carajás, Pará, Brasil, 2023,  Básico, Green Field,  Vale.
• Projeto Novo Sump de Empolpamento de Vargem Grande, Nova Lima, Minas Gerais, Brasil, 2023,  Básico, Green Field,  Vale.
• Projeto Sistema de Proteção Contra Incêndio – Porto Velho, Nova Era, Minas Gerais, Brasil, 2023,  Detalhado, Green Field,  Vale.
• Projeto Atualização da sala de compressores – Área de Moagem e Aditivos – Ponta da Madeira, São Luís, Pará, Brasil, 2023,  Básico, Green Field,  Vale.
• Projeto Sistema de Proteção Contra Incêndio – Centro de Distribuição em Barão de Cocais, Barão de Cocais, Minas Gerais, Brasil, 2023,  Básico, Green Field,  Vale.
• Projeto Reforma do pátio de soldagem – Governador Valadares, Governador Valadares, Minas Gerais, Brasil, 2023,  Detalhado, Green Field,  Vale.
• Projeto Planta de Aspersão do Silo de Carregamento de Timbopeba, Ouro Preto, Minas Gerais, Brasil, 2023,  Detalhado, Green Field,  Vale.
• Projeto Estudo DEM do transportador Link em S11D, Canaã dos Carajás, Minas Gerais, Brasil, 2023,  Básico, Green Field,  Vale.
• Projeto Regularização do AVCB da Mina de Brucutu, São Gonçalo do Rio Abaixo, Minas Gerais, Brasil, 2023,  Detalhado, Green Field,  Vale.`,
        },
        {
          yearRange: "08/2021 - 04/2023",
          title: "Engenheiro de Sistemas de Utilidades",
          company: {
            name: "Brass Engineering",
            url: "https://brassengineering.com/",
          },
          place: "Belo Horizonte, Minas Gerais, Brasil",
          desc: `Desenvolvimento de projetos para a disciplina de Sistemas de Utilidades, incluindo a produção de documentos de projeto como memoriais de cálculo, P&ID, especificações técnicas de equipamentos e serviços, definições de padrões e critérios de projeto, além de acompanhamento constante do planejamento. Condução de reuniões de alinhamento técnico externas e internas, bem como apresentações de projetos para clientes e pares.

Projetos em que participei:

• Projeto Barragem de Rejeito Número 8, Araxá, Minas Gerais, Brasil, 2023,  Básico, Green Field,  CBMM.
• Projeto Barragem de Rejeito Número 9, Araxá, Minas Gerais, Brasil, 2023,  Básico, Green Field,  CBMM.
• Projeto Ampliação do Pátio de Homogeneização, Araxá, Minas Gerais, Brasil, 2023,  Detalhado, Green Field,  CBMM.
• Projeto Relocações de Estruturas de Apoio da Mina de Periquito, Itabira, Minas Gerais, Brasil, 2023,  Conceitual, Green Field,  Vale.
• Projeto Filtragem de Rejeito Total de Timbopeba, Ouro Preto, Minas Gerais, Brasil, 2023,  Conceitual, Green Field,  Vale.
• Projeto SPCI do Sistema de Redução de Umidade do Minério, São Luís, Maranhão, Brasil, 2023,  Básico, Green Field,  Vale.
• Projeto Aumento de Capacidade Produtiva, Oriximiná, Pará, Brasil, 2023,  Conceitual, Green Field,  MRN.
• Projeto Disposição de Rejeito e Drenagem Industrial, Conceição do Mato Dentro, Minas Gerais, Brasil, 2023,  Básico, Green Field,  Anglo American.
• Projeto Dosagem de Reagentes EB2, Conceição do Mato Dentro, Minas Gerais, Brasil, 2023,  Básico, Green Field,  Anglo American.
• Projeto Sistema de Armazenamento de Insumos, Conceição do Mato Dentro, Minas Gerais, Brasil, 2023,  Básico, Green Field,  CBMM.`,
        },
        {
          yearRange: "02/2021 - 08/2021 (7 meses)",
          title: "Engenheiro de Sistemas de Utilidades",
          company: {
            name: "TRENG Consultoria e Tecnologia em Projetos",
            url: "https://trengy.com.br/",
          },
          place: "Belo Horizonte, Minas Gerais, Brasil",
          desc: `Trabalhei no desenvolvimento de documentos técnicos para projetos da disciplina de HVAC, realizando cálculos estruturais pelo método dos elementos finitos (FEM) e analisando estruturas de escoamento pelo método dos elementos discretos (DEM). Fui responsável pela produção de documentos de projeto, incluindo memoriais de cálculo, especificações técnicas de equipamentos e serviços, além da definição de padrões e critérios de projeto. Também conduzi reuniões de alinhamento técnico externas e internas, e realizei apresentações de projetos para clientes e pares.

Projetos em que participei:

• Projeto HVAC da Laminação II, Marabá, Pará, Brasil, 2021,  Detalhado, Green Field,  Sinobras.
• Projeto Revitalização dos Postos de Combustíveis de Salobo, Marabá, Pará, Brasil, 2021,  Detalhado, Green Field,  Vale.
• Projeto Simulação de Fluxo e Remodelagem de Chute de Descarga da TR-1080KS-37, , , Brasil, 2021,  Básico, Green Field,  Vale.
• Projeto Simulação de Fluxo e Remodelagem de Chute de Descarga da TR-1081KS-11@14, , , Brasil, 2021,  Básico, Green Field,  Vale.
• Projeto Ar-Condicionado do Escritório Central Mina da Mutuca, Nova Lima, Minas Gerais, Brasil, 2021,  Básico, Green Field,  Vale.
• Projeto Ar-Condicionado do Restaurante de Brucutu, São Gonçalo do Rio Abaixo, Minas Gerais, Brasil, 2021,  Básico, Green Field,  Vale.
• Projeto Ar-Condicionado do Centro de Treinamento Provisório da Mina de Cauê, Itabira, Minas Gerais, Brasil, 2021,  Detalhado, Green Field,  Vale.
• Projeto Ar-Condicionado do Restaurante da Mina de Conceição, Itabira, Minas Gerais, Brasil, 2021,  Básico, Green Field,  Vale.
• Projeto Ar-Condicionado dos Vestiários da Mina de Conceição, Itabira, Minas Gerais, Brasil, 2021,  Básico, Green Field,  Vale.
• Projeto Ar-Condicionado do Restaurante da Mina de Brucutu, São Gonçalo do Rio Abaixo, Minas Gerais, Brasil, 2021,  Básico, Green Field,  Vale.
• Projeto Simulação de Fluxo e Remodelagem de Chute de Descarga TR-1081KS-11@14, , , Brasil, 2021,  Básico, Green Field,  Vale.`,
        },
        {
          yearRange: "02/2020 - 02/2021 (1 ano, 1 mês)",
          title: "Engenheiro Mecânico",
          company: {
            name: "OpenCAE Engineering",
            url: null,
          },
          place: "Belo Horizonte, Minas Gerais, Brasil",
          desc: "Trabalhei no desenvolvimento de software conceitual DEM para engenharia e na simulação de sistemas de escoamento granular para empresas de mineração, além de estudos FEM para análises estruturais.",
        },
        {
          yearRange: "02/2017 - 02/2020 (3 anos, 1 mês)",
          title: "Engenheiro Mecânico",
          company: {
            name: "Conep Consultoria de Projetos",
            url: "https://conep.com.br/",
          },
          place: "Belo Horizonte, Minas Gerais, Brasil",
          desc: `Trabalhei em projetos envolvendo sistemas estruturais e mecânicos utilizando o método dos elementos finitos (FEM).
Realizei projetos e verifiquei sistemas de transporte de materiais granulares utilizando o método dos elementos discretos (DEM).
Implementei cálculos de projeto e flexibilidade para sistemas de tubulação e bombeamento.
Executei um número considerável de projetos hidráulicos para empresas de mineração e siderurgia.`,
        },
        {
          yearRange: "01/2016 - 01/2017 (1 ano, 1 mês)",
          title: "Vendedor Técnico",
          company: {
            name: "Oxiban Gases Industriais",
            url: null,
          },
          place: "Belo Horizonte, Minas Gerais, Brasil",
          desc: "Fui responsável pela montagem e preparação de equipamentos de soldagem oxiacetilênica. Atuei na prospecção de novos clientes, fechamento de negócios e garantia de atendimento de alta qualidade e fidelização. Também gerenciei o pós-venda e a reposição de estoque para manter níveis adequados de inventário.",
        },
      ],
    },
    skills: {
      title: "Competências",
      items: [
        { name: "Metodologias Ágeis e Waterfall", percent: 100 },
        { name: "Liderança", percent: 100 },
        { name: "Software de código aberto", percent: 100 },
        { name: "Pesquisa", percent: 100 },
        { name: "Resolução de conflitos", percent: 100 },
        { name: "Método dos Elementos Finitos", percent: 100 },
        { name: "Microsoft Excel", percent: 100 },
        { name: "Microsoft Word", percent: 100 },
        { name: "Programação", percent: 100 },
        { name: "Linux", percent: 100 },
        { name: "Python", percent: 100 },
        { name: "C++", percent: 100 },
        { name: "Matlab", percent: 100 },
        { name: "Método dos Elementos Discretos", percent: 100 },
        { name: "Altair HyperWorks", percent: 100 },
        { name: "Paraview", percent: 100 },
        { name: "LIGGGHTS-PUBLIC", percent: 100 },
        { name: "Libmesh", percent: 100 },
        { name: "Code_aster", percent: 100 },
        { name: "Salome-Meca", percent: 100 },
        { name: "Bentley AutoPipe", percent: 100 },
        { name: "AFT Fathom", percent: 100 },
        { name: "HVAC", percent: 100 },
        { name: "Solidworks", percent: 100 },
        { name: "Autocad", percent: 100 },
        { name: "Latex", percent: 100 },
        { name: "Maple", percent: 100 },
        { name: "Proteus Isis", percent: 100 },
        { name: "Arduino", percent: 100 },
        { name: "Raspberry Pi", percent: 100 },
      ],
    },
    languages: {
      title: "Idiomas",
      items: [
        { name: "Português", level: "Nativo", percent: 100 },
        { name: "Inglês", level: "Proficiência profissional", percent: 80 },
        { name: "Francês", level: "Básico", percent: 20 },
      ],
    },
    portfolio: {
      title: "Portfólio",
      all: "Todos",
      category: "Categoria",
    },
    publications: {
      title: "Publicações",
    },
    contact: {
      title: "Contato",
      addressTitle: "Endereço",
      followTitle: "Siga-me",
      sendTitle: "Envie uma mensagem",
      form: {
        name: "Nome",
        email: "E-mail",
        message: "Conte-me mais sobre suas necessidades........",
        submit: "Enviar mensagem",
        sending: "Enviando.....",
      },
      toastSuccess: "Mensagem enviada com sucesso!",
      toastError: "Algo deu errado!",
    },
    footer: {
      rights: "Todos os direitos reservados.",
    },
    general: {
      changeLanguage: "Mudar idioma",
    },
  },
};
